import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Save, Trophy, Medal, Trash2, Ban,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  useRoundScores,
  useRecordScore,
  useUpdateScore,
  useEventStandings,
  useClearRoundScores,
  useTeamScores,
} from '@/api/extensions/useScoringOps';
import { useDrawTeams } from '@/api/extensions/useDrawOps';
import { useEventDetail } from '@/api/extensions/useRodeoEvents';
import type { RoundScoreEntry, StandingEntry } from '@/api/extensions/useScoringOps';
import { resolveEventScoring, timedScoreDecimals } from '@/constants/event-types';

// Render a contestant's name as a link to their user-details page. Falls
// back to plain text when we have no user_profile_id (e.g. unregistered
// write-in partner). Stops click propagation so following the link never
// triggers the surrounding card's keyboard/focus handlers.
function ContestantNameLink({
  name,
  userId,
  className,
}: {
  name: string;
  userId: string | null | undefined;
  className?: string;
}) {
  if (!userId) return <>{name}</>;
  return (
    <Link
      to={`/users/${userId}`}
      className={className ?? 'hover:underline'}
      onClick={(ev) => ev.stopPropagation()}
    >
      {name}
    </Link>
  );
}

interface ScoreEntryPanelProps {
  eventId: string;
  roundNumber: number;
  maxRounds?: number;
  onNextRound?: () => void;
  /** Rodeo only: the active performance the operator is scoring within.
   *  When set, draw teams + round scores + score writes are all scoped
   *  to this performance. Undefined for jackpot events. */
  performanceEventId?: string;
}

export function ScoreEntryPanel({
  eventId,
  roundNumber,
  maxRounds,
  onNextRound,
  performanceEventId,
}: ScoreEntryPanelProps) {
  const [activeIdx, setActiveIdx] = useState(0);
  // Latched: "next time the time input mounts, focus it." Auto-advance to
  // a new round refetches via React Query and briefly swaps the form for
  // a Skeleton, so a one-shot focus effect can fire while the input is
  // unmounted and silently no-op. This ref makes the focus survive the
  // remount.
  const shouldFocusTime = useRef(true);
  const [timeVal, setTimeVal] = useState('');
  const [notesVal, setNotesVal] = useState('');
  const [barrier, setBarrier] = useState<0 | 5 | 10>(0);
  const [leg, setLeg] = useState<0 | 5>(0);
  // Barrel racing: number of barrels tipped (0–3), each a +5s penalty.
  const [barrels, setBarrels] = useState<0 | 1 | 2 | 3>(0);
  const [autoAdvance, setAutoAdvance] = useState(true);
  // Two rodeo-only flags, set before "Save & Next":
  //   dnp — this run is a DID NOT PERFORM (per-run scoring outcome).
  //   excludeFromPrize — pull this ENTRY out of the dynamic prize pool
  //     (per-registration; persists across rounds).
  // They are separate controls but not independent outcomes: saving a DNP takes
  // the entry out of the pool, and un-marking it puts the entry back. The server
  // only acts when DNP actually CHANGES, so the Exclude toggle still lets an
  // admin override the call (keep a DNP contestant's fee in the pot, or pull a
  // performing contestant's out) without the next save reverting them.
  const [dnp, setDnp] = useState(false);
  const [excludeFromPrize, setExcludeFromPrize] = useState(false);
  const [showClearRoundConfirm, setShowClearRoundConfirm] = useState(false);
  // Typed as nullable so the ref callback below can reassign it on
  // mount/unmount cycles (default RefObject has a readonly `current`).
  const timeRef = useRef<HTMLInputElement | null>(null);

  const { data: teamsData, isLoading: teamsLoading } = useDrawTeams(
    eventId,
    performanceEventId ? { performance_event_id: performanceEventId } : undefined,
  );
  const { data: scoresData, isLoading: scoresLoading } = useRoundScores(
    eventId,
    roundNumber,
    performanceEventId,
  );
  const { data: standingsData } = useEventStandings(eventId);
  const { data: eventDetail } = useEventDetail(eventId);


  const recordScore = useRecordScore();
  const updateScore = useUpdateScore();
  const clearRoundScores = useClearRoundScores();

  const teams: any[] = (teamsData as any)?.data ?? (teamsData as unknown as any[]) ?? [];
  const scores: RoundScoreEntry[] = (scoresData as any)?.data ?? (scoresData as unknown as any[]) ?? [];
  const overallStandings: StandingEntry[] = (standingsData as any)?.data ?? (standingsData as unknown as any[]) ?? [];
  // roundStandings now computed from scores array directly (see leaderboard section)

  // Event-type-aware scoring model. Timed events keep the penalty controls;
  // scored events (judged) have none and rank highest-first. Penalty controls
  // also differ by discipline — see resolveEventScoring().
  // Jackpots are always team-roping and carry no discipline metadata, so we
  // only apply event-type logic for rodeo events (identified by the presence
  // of a performance_event). Jackpots keep their existing timed/team-roping UI.
  const isRodeoEvent = !!performanceEventId;
  const scoring = resolveEventScoring(eventDetail as any);
  const isScored = isRodeoEvent ? scoring.isScored : false;
  // Team roping is the only two-rider discipline (and the jackpot default);
  // every other rodeo discipline is a single contestant, so we hide the
  // Header/Heeler framing for them.
  const isTeamRoping = isRodeoEvent ? scoring.isTeamRoping : true;
  // Barrel racing is timed to the thousandth (3 decimals); every other timed
  // event (and jackpots) stays at hundredths.
  const timeDecimals = timedScoreDecimals(scoring.isBarrelRacing);

  const scoreMap = new Map<string, RoundScoreEntry>();
  for (const s of scores) scoreMap.set(s.team_id, s);

  // Per-entry prize-exclusion lookup (lives on the synthetic team / registration,
  // not the run). Drives the "excluded from prize" marker on both leaderboards.
  const excludedById = new Map<string, boolean>(
    teams.map((t: any) => [t.id, !!t.excluded_from_prize_pool]),
  );

  // Filter teams: for rounds > 1, exclude teams eliminated in a PREVIOUS round
  const eligibleTeams = roundNumber > 1
    ? teams.filter((t: any) => {
        // Always include active teams
        if (t.status === 'active') return true;
        // Scratched/disqualified — always exclude
        if (t.status === 'scratched' || t.status === 'disqualified') return false;
        // Eliminated — only exclude if eliminated BEFORE this round
        if (t.status === 'eliminated') {
          const elimRound = t.eliminated_round ?? null;
          if (elimRound != null && elimRound < roundNumber) return false;
          const match = t.elimination_reason?.match(/[Rr]ound (\d+)/);
          if (match && parseInt(match[1]) < roundNumber) return false;
          // Eliminated in current round or later — keep them
          return true;
        }
        return true;
      })
    : teams;

  // Sort by run order. `run_position` is the order the contestants actually
  // run in; it equals the drawn position everywhere except a finals round the
  // delegate has ordered by winnings. Jackpot teams carry no run_position, so
  // fall back to team_number and the order is unchanged for them.
  const runPos = (t: any) => Number(t.run_position ?? t.team_number ?? 0);
  const sorted = [...eligibleTeams].sort((a, b) => runPos(a) - runPos(b));
  // Finals-only money columns. Present (non-null) only when the backend
  // resolved them, which it does for finals events and nothing else.
  const showWinnings = sorted.some(
    (t: any) => t.ytd_winnings != null || t.current_winnings != null,
  );
  const fmtMoney = (v: unknown) =>
    v == null
      ? '—'
      : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  const current = sorted[activeIdx];
  const existing = current ? scoreMap.get(current.id) : null;
  const isUpdate = !!existing;
  const isMutating = recordScore.isPending || updateScore.isPending;

  // Scored count
  const scoredCount = scores.length;
  const totalCount = sorted.length;
  const pct = totalCount > 0 ? Math.round((scoredCount / totalCount) * 100) : 0;

  // Load existing score into form
  useEffect(() => {
    // Prize exclusion is per-entry (lives on the registration / synthetic team),
    // so it reflects the current contestant regardless of whether a run exists.
    setExcludeFromPrize(!!current?.excluded_from_prize_pool);
    setDnp(!!existing?.did_not_perform);
    if (existing) {
      setTimeVal(existing.caught && existing.time_seconds != null ? String(existing.time_seconds) : '');
      setNotesVal(existing.notes ?? '');
      const totalPen = Number(existing.penalty_seconds) || 0;
      // Barrel racing encodes its whole penalty as +5 per tipped barrel.
      if (scoring.isBarrelRacing) {
        const tipped = Math.min(3, Math.round(totalPen / 5)) as 0 | 1 | 2 | 3;
        setBarrels(tipped);
        setBarrier(0);
        setLeg(0);
        if (existing.notes) {
          const clean = existing.notes
            .replace(/Barrels? \+\d+,?\s*/g, '')
            .replace(/^\s*-\s*/, '')
            .trim();
          setNotesVal(clean);
        }
      } else {
        // Parse barrier/leg penalties from notes (falling back to the numeric
        // total when notes are absent).
        let b: 0 | 5 | 10 = 0;
        let l: 0 | 5 = 0;
        if (existing.notes) {
          if (existing.notes.includes('Barrier +10') || existing.notes.includes('Gate +10')) b = 10;
          else if (existing.notes.includes('Barrier +5') || existing.notes.includes('Gate +5')) b = 5;
          if (existing.notes.includes('Leg +5')) l = 5;
          const clean = existing.notes
            .replace(/(Gate|Barrier) \+\d+,?\s*/g, '')
            .replace(/Leg \+\d+,?\s*/g, '')
            .replace(/^\s*-\s*/, '')
            .trim();
          setNotesVal(clean);
        } else {
          if (totalPen === 15) { b = 10; l = 5; }
          else if (totalPen === 10) { b = 10; }
          else if (totalPen === 5) { b = 5; }
        }
        setBarrier(b);
        setLeg(l);
        setBarrels(0);
      }
      if (!existing.caught) setTimeVal('');
    } else {
      setTimeVal('');
      setNotesVal('');
      setBarrier(0);
      setLeg(0);
      setBarrels(0);
    }
  }, [activeIdx, existing?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // When round changes, jump to first unscored team
  const prevRoundRef = useRef(roundNumber);
  useEffect(() => {
    if (prevRoundRef.current !== roundNumber) {
      prevRoundRef.current = roundNumber;
      // Find first team without a score in this round
      const firstUnscored = sorted.findIndex(t => !scoreMap.has(t.id));
      setActiveIdx(firstUnscored >= 0 ? firstUnscored : 0);
    }
  }, [roundNumber, sorted.length, scoreMap.size]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus time input. Refire on roundNumber too — when switching rounds
  // within the same performance, the "jump to first unscored" effect
  // often lands on the same activeIdx (e.g. idx 0), so the focus effect
  // wouldn't re-trigger without an additional dep.
  useEffect(() => {
    shouldFocusTime.current = true;
    if (timeRef.current) {
      setTimeout(() => {
        timeRef.current?.focus();
        timeRef.current?.select();
        shouldFocusTime.current = false;
      }, 100);
    }
    // If timeRef.current is null right now (input was unmounted because
    // useRoundScores is refetching), the ref-callback on remount will
    // pick the latched flag up and focus then.
  }, [activeIdx, roundNumber]);

  const handleSubmit = useCallback(async () => {
    if (!current) return;
    const time = parseFloat(timeVal);
    // Empty or 0 = no catch (timed) / no score (scored).
    const didCatch = !isNaN(time) && time > 0;
    // Penalty model depends on the event discipline. Scored (judged) events
    // never carry penalties — the secretary enters a final score.
    const penNotes: string[] = [];
    let totalPen = 0;
    if (isScored) {
      totalPen = 0;
    } else if (scoring.isBarrelRacing) {
      totalPen = barrels * 5;
      if (barrels > 0) penNotes.push(`Barrels +${totalPen}`);
    } else {
      // Team roping shows Barrier + Leg; other timed events show Barrier only.
      totalPen = barrier + (scoring.isTeamRoping ? leg : 0);
      if (barrier > 0) penNotes.push(`Barrier +${barrier}`);
      if (scoring.isTeamRoping && leg > 0) penNotes.push(`Leg +${leg}`);
    }
    const fullNotes = penNotes.length > 0
      ? `${penNotes.join(', ')}${notesVal ? ` - ${notesVal}` : ''}`
      : notesVal;

    // DNP (rodeo only) overrides the score: no catch, no time/penalty. The
    // exclude-from-prize flag is orthogonal and rides along on every save.
    const isDnp = isRodeoEvent && dnp;
    const payload = {
      eventId,
      roundNumber,
      team_id: current.id,
      caught: isDnp ? false : didCatch,
      time_seconds: isDnp ? undefined : didCatch ? time : undefined,
      penalty_seconds: isDnp ? undefined : didCatch ? totalPen : undefined,
      notes: fullNotes || undefined,
      // Rodeo only — server ignores these for jackpot events.
      did_not_perform: isRodeoEvent ? dnp : undefined,
      excluded_from_prize_pool: isRodeoEvent ? excludeFromPrize : undefined,
      performance_event_id: performanceEventId,
    };

    if (isUpdate) {
      await updateScore.mutateAsync({ ...payload, teamId: current.id });
    } else {
      await recordScore.mutateAsync(payload);
    }

    if (autoAdvance) {
      if (activeIdx < sorted.length - 1) {
        setTimeout(() => setActiveIdx(i => i + 1), 300);
      } else if (onNextRound && maxRounds && roundNumber < maxRounds) {
        // Last team in round — auto-advance to next round
        setTimeout(() => onNextRound(), 500);
      }
    }
  }, [current, timeVal, barrier, leg, barrels, isScored, scoring.isBarrelRacing, scoring.isTeamRoping, notesVal, eventId, roundNumber, performanceEventId, isUpdate, autoAdvance, activeIdx, sorted.length, maxRounds, onNextRound, recordScore, updateScore, dnp, excludeFromPrize, isRodeoEvent]);

  // Keyboard: Enter to save
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Enter') { e.preventDefault(); handleSubmit(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'ArrowRight') { e.preventDefault(); if (activeIdx < sorted.length - 1) setActiveIdx(i => i + 1); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'ArrowLeft') { e.preventDefault(); if (activeIdx > 0) setActiveIdx(i => i - 1); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleSubmit, activeIdx, sorted.length]);

  if (teamsLoading || scoresLoading) {
    return <div className="space-y-3"><Skeleton className="h-16 w-full" /><Skeleton className="h-64 w-full" /></div>;
  }
  if (sorted.length === 0) {
    return <p className="text-sm text-muted-foreground py-4 text-center">No teams available.</p>;
  }

  const names = current?.member_names ?? [];
  // user_profiles.id per member, ordered to match `names` (header first).
  // Backend-aligned for both rodeo and jackpot teams; null entries (e.g.
  // unregistered write-in partners) render as plain text.
  const memberUserIds: (string | null)[] = current?.member_user_ids ?? [];
  const handicaps = current?.member_handicaps ?? [];

  // Gender/age come as individual fields from draw service enrichment
  const headerGender = current?.header_gender ?? null;
  const headerAge = current?.header_age ?? null;
  const heelerGender = current?.heeler_gender ?? null;
  const heelerAge = current?.heeler_age ?? null;

  const fmtGA = (gender: string | null, age: number | null) => {
    const parts: string[] = [];
    if (gender) parts.push(gender.toLowerCase() === 'male' ? 'M' : gender.toLowerCase() === 'female' ? 'F' : gender.charAt(0).toUpperCase());
    if (age != null) parts.push(String(age));
    return parts.length > 0 ? ` (${parts.join(', ')})` : '';
  };

  // Determine card background
  const existingPenalty = existing ? Number(existing.penalty_seconds) || 0 : 0;
  const getCardBg = () => {
    if (!existing) return 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-400 dark:from-blue-950/30 dark:to-indigo-950/30';
    if (existing.did_not_perform) return 'bg-gradient-to-r from-purple-50 to-violet-50 border-purple-400 dark:from-purple-950/30 dark:to-violet-950/30';
    if (!existing.caught) return 'bg-gradient-to-r from-red-50 to-pink-50 border-red-400 dark:from-red-950/30 dark:to-pink-950/30';
    if (existingPenalty > 0) return 'bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-400 dark:from-yellow-950/30 dark:to-orange-950/30';
    return 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-400 dark:from-green-950/30 dark:to-emerald-950/30';
  };

  // Current team's placement among scored teams
  const currentPlacement = current ? overallStandings.findIndex(s => s.team_id === current.id) + 1 : 0;
  // "X of Y" — Y is how many teams are in this round (sorted = eligible teams for current round)
  const scoredTeamCount = sorted.length;
  const placementIcon = currentPlacement === 1 ? '🏆' : currentPlacement === 2 ? '🥈' : currentPlacement === 3 ? '🥉' : null;

  // Slide adjustment calculation for current team
  const ev = eventDetail as any;
  const slideType = ev?.slide_type ?? 'NONE';
  const slideBase = Number(ev?.slide_base_handicap) || 0;
  const slideRate = Number(ev?.slide_conversion_rate) || 0.5;
  const teamHC = current ? Number(current.combined_handicap) || 0 : 0;
  const slideAdj = (() => {
    if (slideType === 'NONE' || !slideBase) return 0;
    const diff = teamHC - slideBase;
    if (slideType === 'SLIDE_DOWN') return diff < 0 ? diff * 2 * slideRate : 0;
    if (slideType === 'SLIDE_UP_DOWN') return diff * 2 * slideRate;
    return 0;
  })();

  // Need-for calculation
  const calcNeeded = (place: number): string => {
    if (overallStandings.length < place) return '--';
    const target = overallStandings[place - 1];
    if (!target || target.total_time == null) return '--';
    return Number(target.total_time).toFixed(timeDecimals);
  };

  // Progress bar visible window
  const visibleCount = 9;
  const half = Math.floor(visibleCount / 2);
  let startIdx = 0;
  if (sorted.length <= visibleCount) startIdx = 0;
  else if (activeIdx < half) startIdx = 0;
  else if (activeIdx > sorted.length - half - 1) startIdx = Math.max(0, sorted.length - visibleCount);
  else startIdx = activeIdx - half;
  const endIdx = Math.min(startIdx + visibleCount, sorted.length);
  const visibleTeams = sorted.slice(startIdx, endIdx);

  // Team carousel (bottom) — show 2 prev, current, 2 next
  const carouselStart = Math.max(0, activeIdx - 2);
  const carouselEnd = Math.min(sorted.length, activeIdx + 3);
  void carouselStart;
  void carouselEnd;

  return (
    <div className="space-y-3">
      {/* ─── Progress Bar ───────────────────────────────── */}
      <div className="rounded-lg border p-3 bg-muted/30">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium">Round {roundNumber} Progress</span>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">
                {showWinnings ? 'Jump to run #:' : 'Jump to:'}
              </span>
              <input
                type="number" min={1} max={sorted.length}
                className="w-10 px-1 py-0 text-xs border rounded bg-background"
                placeholder={String(activeIdx + 1)}
                onBlur={(e) => {
                  const v = parseInt(e.target.value) - 1;
                  if (v >= 0 && v < sorted.length) setActiveIdx(v);
                  e.target.value = '';
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
              />
              <span className="text-xs text-muted-foreground">/ {sorted.length}</span>
            </div>
            <span className="text-xs text-muted-foreground">{scoredCount} Complete ({pct}%)</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex gap-0.5">
            <button onClick={() => setActiveIdx(Math.max(0, activeIdx - 10))} disabled={activeIdx === 0}
              className="px-1 py-1 bg-muted hover:bg-muted/80 disabled:opacity-30 rounded flex items-center">
              <ChevronLeft className="h-3 w-3" /><ChevronLeft className="h-3 w-3 -ml-1.5" />
            </button>
            <button onClick={() => setActiveIdx(Math.max(0, activeIdx - 1))} disabled={activeIdx === 0}
              className="p-1 bg-muted hover:bg-muted/80 disabled:opacity-30 rounded">
              <ChevronLeft className="h-4 w-4" />
            </button>
          </div>

          <div className="flex gap-[2px] h-10 rounded overflow-hidden flex-1">
            {visibleTeams.map((team, idx) => {
              const ai = startIdx + idx;
              const sc = scoreMap.get(team.id);
              let bg = 'bg-gray-300 dark:bg-gray-600';
              let ring = '';
              if (ai === activeIdx) { bg = 'bg-blue-500 animate-pulse'; ring = 'ring-2 ring-blue-700 ring-inset'; }
              else if (sc) {
                // DNP gets its own color (purple) so it's instantly distinct
                // from a plain no-score (red) and a penalty (yellow).
                if (sc.did_not_perform) bg = 'bg-purple-500';
                else if (!sc.caught) bg = 'bg-red-500';
                else if (Number(sc.penalty_seconds) > 0) bg = 'bg-yellow-500';
                else bg = 'bg-green-500';
              }
              // Check if this team is top 3 in overall standings
              const teamPlace = overallStandings.findIndex(s => s.team_id === team.id && s.successful_catches > 0) + 1;
              const barTrophy = teamPlace === 1 ? '🏆' : teamPlace === 2 ? '🥈' : teamPlace === 3 ? '🥉' : null;
              const isExcluded = !!team.excluded_from_prize_pool;
              return (
                <div key={team.id} className={`${bg} ${ring} hover:opacity-80 cursor-pointer transition-all flex-1 flex flex-col items-center justify-center`}
                  onClick={() => setActiveIdx(ai)}
                  title={isExcluded ? 'Excluded from prize pool' : undefined}>
                  <span className="text-[10px] text-white font-bold">#{team.run_position ?? team.team_number}</span>
                  {(barTrophy || isExcluded) && (
                    <span className="flex items-center gap-0.5 leading-none">
                      {barTrophy && <span className="text-[10px] leading-none">{barTrophy}</span>}
                      {isExcluded && <Ban className="h-2.5 w-2.5 text-white" aria-label="Excluded from prize pool" />}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex gap-0.5">
            <button onClick={() => setActiveIdx(Math.min(sorted.length - 1, activeIdx + 1))} disabled={activeIdx >= sorted.length - 1}
              className="p-1 bg-muted hover:bg-muted/80 disabled:opacity-30 rounded">
              <ChevronRight className="h-4 w-4" />
            </button>
            <button onClick={() => setActiveIdx(Math.min(sorted.length - 1, activeIdx + 10))} disabled={activeIdx >= sorted.length - 1}
              className="px-1 py-1 bg-muted hover:bg-muted/80 disabled:opacity-30 rounded flex items-center">
              <ChevronRight className="h-3 w-3" /><ChevronRight className="h-3 w-3 -ml-1.5" />
            </button>
          </div>
        </div>

        <div className="flex gap-4 mt-2 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-green-500 rounded" />{isScored ? 'Scored' : 'Clean'}</span>
          {!isScored && <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-yellow-500 rounded" />Penalty</span>}
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-red-500 rounded" />{isScored ? 'No Score' : 'No Time'}</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-purple-500 rounded" />DNP</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-blue-500 rounded" />Current</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-gray-300 dark:bg-gray-600 rounded" />Pending</span>
          <span className="flex items-center gap-1"><Ban className="h-2.5 w-2.5" />Excluded from prize</span>
        </div>
      </div>

      {/* ─── Main Grid: Left (scoring + carousel) + Right (leaderboards) ── */}
      <div className="grid grid-cols-12 gap-3">
        {/* Left: Scoring Card + Carousel */}
        <div className="col-span-7 flex flex-col gap-3">
          <div className={`rounded-lg border-2 px-4 py-2.5 flex flex-col ${getCardBg()}`} style={{ minHeight: '420px' }}>
            {/* Top row: badges left, placement centered, NEED FOR absolute top-right */}
            <div className="relative flex items-center gap-2 mb-0">
              {/* Left: score/status badges */}
              <div className="flex items-center gap-1.5 flex-wrap">
                {!existing ? (
                  <>
                    <span className="px-2 py-0.5 bg-blue-600 text-white text-[11px] rounded-full font-semibold animate-pulse">NOW RUNNING</span>
                    {slideAdj !== 0 && (
                      <span className={`px-1.5 py-0.5 text-[11px] rounded-full font-semibold border ${
                        slideAdj > 0 ? 'bg-red-100 text-red-700 border-red-300' : 'bg-green-100 text-green-700 border-green-300'
                      }`}>Slide {slideAdj > 0 ? '+' : ''}{slideAdj.toFixed(1)}s</span>
                    )}
                  </>
                ) : existing.caught ? (
                  <>
                    <span className={`px-1.5 py-0.5 text-white text-[11px] rounded-full font-semibold ${existingPenalty > 0 ? 'bg-yellow-600' : 'bg-green-600'}`}>
                      {Number(existing.time_seconds).toFixed(timeDecimals)}{isScored ? ' pts' : 's'}{!isScored && existingPenalty > 0 && ` (+${existingPenalty})`}
                    </span>
                    {slideAdj !== 0 && (
                      <span className={`px-1.5 py-0.5 text-[11px] rounded-full font-semibold border ${
                        slideAdj > 0 ? 'bg-red-100 text-red-700 border-red-300' : 'bg-green-100 text-green-700 border-green-300'
                      }`}>Slide {slideAdj > 0 ? '+' : ''}{slideAdj.toFixed(1)}s</span>
                    )}
                    {(existingPenalty > 0 || slideAdj !== 0) && (
                      <span className="px-1.5 py-0.5 bg-gray-800 text-white text-[11px] rounded-full font-semibold dark:bg-gray-200 dark:text-gray-900">
                        = {Number(existing.final_time ?? ((Number(existing.time_seconds) || 0) + existingPenalty + slideAdj)).toFixed(timeDecimals)}s
                      </span>
                    )}
                  </>
                ) : existing.did_not_perform ? (
                  <span className="px-2 py-0.5 bg-purple-500 text-white text-[11px] rounded-full font-semibold">DNP</span>
                ) : (
                  <span className="px-2 py-0.5 bg-red-600 text-white text-[11px] rounded-full font-semibold">{isScored ? 'NO SCORE' : 'NO TIME'}</span>
                )}
              </div>

              {/* Center: Overall placement — absolutely centered in the box */}
              {currentPlacement > 0 && scoredTeamCount > 0 && existing?.caught && (
                <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-1">
                  {placementIcon && <span className="text-lg">{placementIcon}</span>}
                  <span className={`text-lg font-black ${currentPlacement <= 3 ? 'text-yellow-600' : 'text-foreground'}`}>
                    {currentPlacement}{currentPlacement === 1 ? 'st' : currentPlacement === 2 ? 'nd' : currentPlacement === 3 ? 'rd' : 'th'}
                  </span>
                  <span className="text-sm font-bold text-muted-foreground">of {scoredTeamCount}</span>
                </div>
              )}

              {/* Right: NEED FOR — absolute so it doesn't stretch row height */}
              <div className="absolute top-0 right-0 text-right text-xs leading-snug">
                <p className="text-muted-foreground font-semibold text-[11px]">NEED FOR:</p>
                <p>1st: <span className="font-mono font-black text-base text-foreground">{calcNeeded(1)}</span></p>
                <p>2nd: <span className="font-mono font-black text-base text-foreground">{calcNeeded(2)}</span></p>
                <p>3rd: <span className="font-mono font-black text-base text-foreground">{calcNeeded(3)}</span></p>
              </div>
            </div>

            <h2 className="text-xl font-bold mt-1.5">
              {isTeamRoping
                ? `Team #${current?.team_number}`
                : (names[0]
                    ? <ContestantNameLink name={names[0]} userId={memberUserIds[0]} />
                    : `Contestant #${current?.team_number}`)}
              {!isTeamRoping && current?.team_number != null && names[0] && (
                <span className="ml-2 text-sm font-medium text-muted-foreground">#{current.team_number}</span>
              )}
            </h2>

            {/* Finals: the two figures the run order is derived from. YTD is
                the season board at this association; Current is what this
                entry has won at THIS rodeo through its completed rounds. */}
            {showWinnings && (
              <div className="flex items-center gap-3 mt-0.5 text-xs">
                <span className="text-muted-foreground">
                  YTD <span className="font-mono font-semibold text-foreground">{fmtMoney(current?.ytd_winnings)}</span>
                </span>
                <span className="text-muted-foreground">
                  Current <span className="font-mono font-semibold text-foreground">{fmtMoney(current?.current_winnings)}</span>
                </span>
                {current?.run_position != null && current?.draw_position != null &&
                  Number(current.run_position) !== Number(current.draw_position) && (
                    <span className="text-muted-foreground">
                      Run #{current.run_position} · Draw #{current.draw_position}
                    </span>
                  )}
              </div>
            )}

            {/* Contestant(s) — Header/Heeler split and handicaps are team-roping
                concepts; single-contestant disciplines show just the rider. */}
            <div className="mb-3 space-y-0.5">
              {isTeamRoping ? (
                <>
                  <p className="text-sm">
                    <span className="font-medium">
                      <ContestantNameLink name={names[0] ?? 'Header'} userId={memberUserIds[0]} />
                    </span>
                    <span className="text-muted-foreground">{fmtGA(headerGender, headerAge)}</span>
                    <span className="text-muted-foreground ml-2">(Header{handicaps[0] != null ? `: ${Number(handicaps[0]).toFixed(1)}` : ''})</span>
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">
                      <ContestantNameLink name={names[1] ?? 'Heeler'} userId={memberUserIds[1]} />
                    </span>
                    <span className="text-muted-foreground">{fmtGA(heelerGender, heelerAge)}</span>
                    <span className="text-muted-foreground ml-2">(Heeler{handicaps[1] != null ? `: ${Number(handicaps[1]).toFixed(1)}` : ''})</span>
                  </p>
                  {current?.combined_handicap != null && (
                    <p className="text-sm font-medium text-blue-600 dark:text-blue-400">
                      Combined HC: {Number(current.combined_handicap).toFixed(1)}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm">
                  <span className="text-muted-foreground">{fmtGA(headerGender, headerAge)}</span>
                </p>
              )}
            </div>

            {/* Previous Rounds — show scores from overall standings data */}
            {roundNumber > 1 && current && (
              <PreviousRounds eventId={eventId} teamId={current.id} currentRound={roundNumber} isScored={isScored} decimals={timeDecimals} />
            )}

            {/* Spacer pushes form to bottom */}
            <div className="flex-1" />

            {/* Time/Score + Notes */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">{isScored ? 'Score' : 'Time'}</label>
                <Input
                  ref={(el) => {
                    timeRef.current = el;
                    // If we owed a focus (latched while the input was
                    // unmounted during a score refetch), claim it now.
                    if (el && shouldFocusTime.current) {
                      el.focus();
                      el.select();
                      shouldFocusTime.current = false;
                    }
                  }}
                  type="number" step={isScored ? '0.01' : scoring.isBarrelRacing ? '0.001' : '0.01'} min={0} value={timeVal}
                  onChange={(e) => setTimeVal(e.target.value)} placeholder={scoring.isBarrelRacing && !isScored ? '0.000' : '0.00'}
                  className="h-9 text-sm font-mono" autoComplete="off" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Notes</label>
                <Input value={notesVal} onChange={(e) => setNotesVal(e.target.value)}
                  placeholder="Optional..." className="h-9 text-sm" />
              </div>
            </div>

            {/* Penalty controls — conditional on event type. Scored (judged)
                events have no penalties; the layout footprint is held by a
                fixed-height row so the card never resizes between disciplines. */}
            <div className="grid grid-cols-2 gap-4 mb-3 min-h-[3.25rem]">
              {isScored ? (
                <div className="col-span-2 flex items-center justify-center text-xs text-muted-foreground italic">
                  Scored event — enter the final judged score (no penalties)
                </div>
              ) : scoring.isBarrelRacing ? (
                <div className="col-span-2">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">
                    Barrels Tipped (+5 each)
                  </label>
                  <div className="flex gap-1">
                    {([0, 1, 2, 3] as const).map(v => {
                      const active = barrels === v;
                      const color = active
                        ? v === 0 ? 'bg-green-600 text-white border-green-800' : 'bg-yellow-500 text-white border-yellow-700'
                        : 'bg-muted hover:bg-muted/80 text-foreground border-gray-400';
                      return (
                        <button key={v} onClick={() => setBarrels(v)}
                          className={`flex-1 py-1.5 text-sm font-medium rounded border-2 transition-colors ${color}`}>
                          {v === 0 ? 'None' : `${v} (+${v * 5})`}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <>
                  <div className={scoring.isTeamRoping ? '' : 'col-span-2'}>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Barrier</label>
                    <div className="flex gap-1">
                      {([0, 5, 10] as const).map(v => {
                        const active = barrier === v;
                        const color = active
                          ? v === 0 ? 'bg-green-600 text-white border-green-800' : v === 5 ? 'bg-yellow-500 text-white border-yellow-700' : 'bg-red-600 text-white border-red-800'
                          : 'bg-muted hover:bg-muted/80 text-foreground border-gray-400';
                        return (
                          <button key={v} onClick={() => setBarrier(v)}
                            className={`flex-1 py-1.5 text-sm font-medium rounded border-2 transition-colors ${color}`}>
                            {v === 0 ? 'None' : `+${v}`}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {scoring.isTeamRoping && (
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Leg</label>
                      <div className="flex gap-1">
                        {([0, 5] as const).map(v => {
                          const active = leg === v;
                          const color = active
                            ? v === 0 ? 'bg-green-600 text-white border-green-800' : 'bg-yellow-500 text-white border-yellow-700'
                            : 'bg-muted hover:bg-muted/80 text-foreground border-gray-400';
                          return (
                            <button key={v} onClick={() => setLeg(v)}
                              className={`flex-1 py-1.5 text-sm font-medium rounded border-2 transition-colors ${color}`}>
                              {v === 0 ? 'None' : `+${v}`}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Rodeo-only flags set before Save. DNP marks the run
                did-not-perform AND flips Exclude on next to it — a contestant
                who didn't perform has no money in the pot. Exclude stays
                clickable on its own, so an admin can still override the call
                either way before saving. */}
            {isRodeoEvent && (
              <div className="grid grid-cols-2 gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => {
                    const next = !dnp;
                    setDnp(next);
                    setExcludeFromPrize(next);
                  }}
                  aria-pressed={dnp}
                  title="Mark this contestant as Did Not Perform — shown as DNP on the leaderboards, and their entry comes OUT of the prize pool (un-mark to put it back)"
                  className={`py-1.5 text-xs font-semibold rounded border-2 transition-colors ${
                    dnp
                      ? 'bg-purple-600 text-white border-purple-800'
                      : 'bg-muted hover:bg-muted/80 text-foreground border-gray-400'
                  }`}
                >
                  {dnp ? '✓ Did Not Perform' : 'Did Not Perform'}
                </button>
                <button
                  type="button"
                  onClick={() => setExcludeFromPrize((v) => !v)}
                  aria-pressed={excludeFromPrize}
                  title="Exclude this entry from the dynamic prize-pool calculation. Turns on automatically with Did Not Perform — toggle it back off to keep this contestant's fee in the pot anyway."
                  className={`py-1.5 text-xs font-semibold rounded border-2 transition-colors ${
                    excludeFromPrize
                      ? 'bg-slate-700 text-white border-slate-900'
                      : 'bg-muted hover:bg-muted/80 text-foreground border-gray-400'
                  }`}
                >
                  {excludeFromPrize ? '✓ Excluded from Prize' : 'Exclude from Prize'}
                </button>
              </div>
            )}

            {/* Auto-advance + Nav + Save */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={autoAdvance} onChange={(e) => setAutoAdvance(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300" />
                  Auto-advance
                </label>
                <button onClick={() => setShowClearRoundConfirm(true)}
                  className="text-[10px] text-red-500 hover:text-red-700 flex items-center gap-0.5" title="Clear all scores for this round">
                  <Trash2 className="h-3 w-3" /> Clear Round
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => activeIdx > 0 && setActiveIdx(i => i - 1)} disabled={activeIdx === 0}
                  className="p-1.5 bg-muted rounded hover:bg-muted/80 disabled:opacity-30">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button onClick={() => activeIdx < sorted.length - 1 && setActiveIdx(i => i + 1)} disabled={activeIdx >= sorted.length - 1}
                  className="p-1.5 bg-muted rounded hover:bg-muted/80 disabled:opacity-30">
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button onClick={handleSubmit} disabled={isMutating}
                  className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5">
                  <Save className="h-4 w-4" />
                  Save & Next
                </button>
              </div>
            </div>
          </div>

          {/* Team Carousel — 5 cards, same width as scoring card */}
          <div className="grid grid-cols-5 gap-2">
            {(() => {
              const cStart = Math.max(0, Math.min(activeIdx - 2, sorted.length - 5));
              const cEnd = Math.min(sorted.length, cStart + 5);
              return sorted.slice(cStart, cEnd).map((team) => {
                const idx = sorted.indexOf(team);
                const sc = scoreMap.get(team.id);
                const isCurrent = idx === activeIdx;
                const isPrev = idx < activeIdx;
                const tNames = team.member_names ?? [];
                let borderColor = 'border-gray-200 dark:border-gray-700';
                let bgColor = 'bg-card hover:bg-muted/50';
                let label = 'NEXT';
                if (isCurrent) { borderColor = 'border-blue-500'; bgColor = 'bg-blue-50 dark:bg-blue-950/30'; label = 'CURRENT'; }
                else if (isPrev) { label = 'PREV'; }

                return (
                  <div key={team.id}
                    onClick={() => setActiveIdx(idx)}
                    className={`rounded-lg border-2 p-2.5 cursor-pointer transition-all ${borderColor} ${bgColor}`}>
                    <p className={`text-[10px] font-bold uppercase ${isCurrent ? 'text-blue-600' : 'text-muted-foreground'}`}>{label}</p>
                    <p className="font-bold text-base">#{team.run_position ?? team.team_number}</p>
                    {showWinnings && (
                      <p className="text-[10px] text-muted-foreground leading-tight">
                        YTD {fmtMoney(team.ytd_winnings)} · Cur {fmtMoney(team.current_winnings)}
                      </p>
                    )}
                    {sc && !sc.caught && sc.did_not_perform && <p className="text-purple-600 font-bold text-xs">DNP</p>}
                    {sc && !sc.caught && !sc.did_not_perform && <p className="text-red-600 font-bold text-xs">{isScored ? 'NO SCORE' : 'NO TIME'}</p>}
                    {sc && sc.caught && (
                      <p className="text-green-600 font-bold text-xs">
                        {Number(sc.final_time ?? ((Number(sc.time_seconds) || 0) + (Number(sc.penalty_seconds) || 0))).toFixed(timeDecimals)}{isScored ? '' : 's'}
                      </p>
                    )}
                    {!sc && <p className="text-muted-foreground text-xs">&nbsp;</p>}
                    <div className="mt-1 border-t pt-1 border-border/50">
                      <p className="text-[10px] text-muted-foreground truncate">{tNames[0] ?? '—'}</p>
                      {isTeamRoping && <p className="text-[10px] text-muted-foreground truncate">{tNames[1] ?? '—'}</p>}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>

        {/* Right: Overall + Round Leaderboards — capped height, scrolls internally */}
        <div className="col-span-5 grid grid-cols-2 gap-2 overflow-hidden" style={{ maxHeight: '560px' }}>
          <LeaderboardCard title="Overall" icon={<Trophy className="h-4 w-4 text-yellow-500" />} showCatches showNoTime noPenaltyHighlight
            isTeamRoping={isTeamRoping} isScored={isScored} decimals={timeDecimals}
            entries={overallStandings.filter(s => s.total_runs > 0).map(s => ({
              ...s,
              excluded_from_prize_pool: !!(s.excluded_from_prize_pool || excludedById.get(s.team_id)),
            }))} scoreMap={scoreMap} />
          <LeaderboardCard title={`Round ${roundNumber}`} icon={<Medal className="h-4 w-4 text-blue-500" />} showNoTime
            isTeamRoping={isTeamRoping} isScored={isScored} decimals={timeDecimals}
            entries={(() => {
              // Build round-specific entries from actual round scores (not cumulative standings)
              const roundEntries: StandingEntry[] = scores
                .map((sc, _i) => ({
                  placement: 0,
                  team_id: sc.team_id,
                  team_number: sc.team_number,
                  member_names: sc.member_names,
                  combined_handicap: sc.combined_handicap,
                  status: sc.team_status,
                  total_runs: 1,
                  successful_catches: sc.caught ? 1 : 0,
                  total_time: sc.final_time,
                  average_time: sc.final_time,
                  best_time: sc.time_seconds != null ? Number(sc.time_seconds) : null,
                  advanced_to_short_round: false,
                  did_not_perform: !!sc.did_not_perform,
                  excluded_from_prize_pool: !!excludedById.get(sc.team_id),
                }))
                .sort((a, b) => {
                  // DNP entries sink to the very bottom (below NT/NS).
                  if (!!a.did_not_perform !== !!b.did_not_perform) return a.did_not_perform ? 1 : -1;
                  // Caught/scored teams first; then by final time (timed:
                  // lowest first) or score (scored: highest first).
                  if (a.successful_catches !== b.successful_catches) return b.successful_catches - a.successful_catches;
                  const at = Number(a.total_time);
                  const bt = Number(b.total_time);
                  if (isScored) return (isNaN(bt) ? -Infinity : bt) - (isNaN(at) ? -Infinity : at);
                  return (isNaN(at) ? Infinity : at) - (isNaN(bt) ? Infinity : bt);
                });
              // Assign placements
              let place = 1;
              roundEntries.forEach(e => { e.placement = e.successful_catches > 0 ? place++ : 0; });
              return roundEntries;
            })()} scoreMap={scoreMap} />
        </div>
      </div>

      {/* Clear Round Confirmation */}
      <Dialog open={showClearRoundConfirm} onOpenChange={setShowClearRoundConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear Round {roundNumber} Scores</DialogTitle>
            <DialogDescription>
              This will permanently delete all {scores.length} score(s) recorded for Round {roundNumber}. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClearRoundConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={async () => {
              await clearRoundScores.mutateAsync({
                eventId,
                roundNumber,
                performanceEventId,
              });
              setShowClearRoundConfirm(false);
              setActiveIdx(0);
            }} disabled={clearRoundScores.isPending}>
              {clearRoundScores.isPending ? 'Deleting...' : `Delete ${scores.length} Score(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Leaderboard Card ───────────────────────────────────────────

function LeaderboardCard({
  title,
  icon,
  entries,
  showCatches,
  showNoTime,
  noPenaltyHighlight,
  scoreMap,
  isTeamRoping = true,
  isScored = false,
  decimals = 2,
}: {
  title: string;
  icon: React.ReactNode;
  entries: StandingEntry[];
  showCatches?: boolean;
  showNoTime?: boolean;
  noPenaltyHighlight?: boolean;
  scoreMap?: Map<string, RoundScoreEntry>;
  isTeamRoping?: boolean;
  isScored?: boolean;
  /** Decimal places for displayed times — 3 for barrel racing, else 2. */
  decimals?: number;
}) {
  const empty = entries.length === 0;

  return (
    <div className="rounded-lg border p-2 flex flex-col h-full min-h-0">
      {/* Header — larger, underlined */}
      <div className="flex items-center gap-1.5 pb-1.5 mb-1.5 border-b-2 border-foreground/20 shrink-0">
        {icon}
        <span className="text-sm font-bold">{title}</span>
      </div>

      {empty ? (
        <p className="text-xs text-muted-foreground text-center py-6 flex-1 flex items-center justify-center">
          No times recorded yet
        </p>
      ) : (
        <div className="space-y-1 overflow-y-auto flex-1 min-h-0">
          {entries.slice(0, 50).map((s) => {
            const isDnp = !!s.did_not_perform;
            const isNT = !isDnp && (s.total_time == null || s.successful_catches === 0);
            // Check penalties from actual round score data if available
            const roundScore = scoreMap?.get(s.team_id);
            const penaltyVal = roundScore ? Number(roundScore.penalty_seconds) || 0 : 0;
            const hasPenalty = !isNT && !isDnp && penaltyVal > 0;
            const baseTime = roundScore && roundScore.time_seconds != null ? Number(roundScore.time_seconds) : null;
            // DNP never places, so it never carries a medal badge.
            const placeIcon = isDnp ? null : s.placement === 1 ? '🏆' : s.placement === 2 ? '🥈' : s.placement === 3 ? '🥉' : null;

            // Background: purple for DNP, red for NT, yellow/orange for penalty.
            let cardBorder = 'border-gray-800 dark:border-gray-300';
            let cardBg = '';
            if (isDnp) {
              cardBorder = 'border-purple-400 dark:border-purple-600';
              cardBg = 'bg-purple-50 dark:bg-purple-950/20';
            } else if (isNT && showNoTime) {
              cardBorder = 'border-red-400 dark:border-red-700';
              cardBg = 'bg-red-50 dark:bg-red-950/20';
            } else if (hasPenalty && !noPenaltyHighlight) {
              cardBorder = 'border-yellow-500 dark:border-yellow-600';
              cardBg = 'bg-yellow-50 dark:bg-yellow-950/20';
            }

            return (
              <div key={s.team_id}
                className={`rounded border-2 px-2 py-1 ${cardBorder} ${cardBg}`}
                style={{ height: '74px', overflow: 'hidden' }}>
                <div className="flex h-full">
                  {/* Left: placement, team, members */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      {placeIcon && <span className="text-xs">{placeIcon}</span>}
                      <span className={`text-xs font-bold ${
                        isDnp ? 'text-purple-600' : s.placement === 1 ? 'text-yellow-600' : s.placement === 2 ? 'text-gray-500' : s.placement === 3 ? 'text-orange-600' : ''
                      }`}>
                        {isDnp ? 'DNP' : isNT ? 'NT' : s.placement}
                      </span>
                      {s.excluded_from_prize_pool && (
                        <span title="Excluded from prize pool" aria-label="Excluded from prize pool" className="inline-flex">
                          <Ban className="h-3 w-3 text-slate-500" />
                        </span>
                      )}
                    </div>
                    {isTeamRoping ? (
                      <>
                        <p className="text-[11px] font-semibold truncate">Team #{s.team_number ?? '—'}</p>
                        <p className="text-[10px] text-muted-foreground truncate">Header: {s.member_names?.[0] ?? '—'}</p>
                        <p className="text-[10px] text-muted-foreground truncate">Heeler: {s.member_names?.[1] ?? '—'}</p>
                      </>
                    ) : (
                      <>
                        <p className="text-[11px] font-semibold truncate">{s.member_names?.[0] ?? `#${s.team_number ?? '—'}`}</p>
                        <p className="text-[10px] text-muted-foreground truncate">#{s.team_number ?? '—'}</p>
                      </>
                    )}
                  </div>
                  {/* Right: time + penalty + catches */}
                  <div className="shrink-0 text-right pl-1 flex flex-col justify-start">
                    <span className="text-xs font-mono font-bold">
                      {isDnp ? (
                        <span className="text-purple-600">DNP</span>
                      ) : isNT ? (
                        <span className="text-red-600">{isScored ? 'NO SCORE' : 'NO TIME'}</span>
                      ) : (
                        Number(s.total_time).toFixed(decimals)
                      )}
                    </span>
                    {!isScored && hasPenalty && !noPenaltyHighlight && baseTime != null && (
                      <span className="text-[9px] text-yellow-700 dark:text-yellow-400 font-mono">
                        {baseTime.toFixed(decimals)}+{penaltyVal}
                      </span>
                    )}
                    {showCatches && !isNT && (
                      <span className="text-[9px] text-muted-foreground">{s.successful_catches} {isScored ? 'scored' : 'catch'}</span>
                    )}
                    {isNT && s.total_runs > 0 && (
                      <span className="text-[9px] text-muted-foreground">
                        {s.total_runs - s.successful_catches} no {isScored ? 'score' : 'time'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Previous Rounds (separate component to isolate useTeamScores hook) ──

function PreviousRounds({ eventId, teamId, currentRound, isScored = false, decimals = 2 }: { eventId: string; teamId: string; currentRound: number; isScored?: boolean; decimals?: number }) {
  const { data: teamScoresData } = useTeamScores(eventId, teamId);
  const scores: any[] = (teamScoresData as any)?.data ?? (teamScoresData as unknown as any[]) ?? [];

  if (scores.length === 0) return null;

  return (
    <div className="mb-2 pt-2 border-t border-border/50">
      <p className="text-xs font-bold text-muted-foreground mb-1">PREVIOUS ROUNDS</p>
      <div className="flex gap-4 flex-wrap">
        {Array.from({ length: currentRound - 1 }, (_, i) => i + 1).map(rn => {
          const s = scores.find((sc: any) => sc.round_number === rn);
          if (!s) return null;
          const finalT = s.final_time != null ? Number(s.final_time) : null;
          return (
            <div key={rn} className="text-sm">
              <span className="text-muted-foreground">R{rn}:</span>{' '}
              {s.caught && finalT != null ? (
                <span className="font-bold">{finalT.toFixed(decimals)}</span>
              ) : s.did_not_perform ? (
                <span className="font-bold text-purple-600">DNP</span>
              ) : (
                <span className="font-bold text-red-600">{isScored ? 'NS' : 'NT'}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
